import { useId, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AdminColumnFilterValue, AdminColumnOption } from "./admin-column-filter-types"

import { ArrowDown, ArrowUp, Filter } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

/** Shared Excel-style header menu; data loading and predicates remain owned by each table. */
export function AdminColumnFilter({
  label,
  value,
  options,
  textSearch = false,
  numericRange = false,
  excludeFromAll = false,
  active,
  sortDirection,
  onSort,
  onApply,
  onClear,
}: {
  label: string
  value: AdminColumnFilterValue
  options?: AdminColumnOption[]
  textSearch?: boolean
  numericRange?: boolean
  /** Unchecking from all records an exclusion instead of copying the loaded options. */
  excludeFromAll?: boolean
  active: boolean
  sortDirection?: "asc" | "desc"
  onSort?: (direction: "asc" | "desc") => void
  onApply: (value: AdminColumnFilterValue) => void
  onClear: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [optionSearch, setOptionSearch] = useState("")
  const inputId = useId()
  const visibleOptions = options?.filter((option) =>
    option.label.toLocaleLowerCase().includes(optionSearch.toLocaleLowerCase())
  )
  const hasMinimum = Boolean(draft.minimum?.trim())
  const hasMaximum = Boolean(draft.maximum?.trim())
  const minimum = Number(draft.minimum)
  const maximum = Number(draft.maximum)
  const invalidRange =
    numericRange &&
    ((hasMinimum && !Number.isFinite(minimum)) ||
      (hasMaximum && !Number.isFinite(maximum)) ||
      (hasMinimum && hasMaximum && minimum > maximum))

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          const nextDraft: AdminColumnFilterValue = {
            ...value,
            selected: value.selected ? [...value.selected] : value.selected,
          }
          if (value.excluded != null) nextDraft.excluded = [...value.excluded]
          setDraft(nextDraft)
          setOptionSearch("")
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("adminDashboard.columnFilters.menu", "Filter {{column}}", {
            column: label,
          })}
          className={cn(
            "focus-visible:ring-ring hover:bg-accent flex min-h-10 w-full items-center justify-between gap-2 rounded-md px-2 text-left font-medium focus-visible:ring-2 focus-visible:outline-none",
            active && "bg-accent text-accent-foreground"
          )}
        >
          <span>{label}</span>
          <span className="flex shrink-0 items-center gap-1">
            {sortDirection === "asc" && <ArrowUp aria-hidden="true" className="size-3.5" />}
            {sortDirection === "desc" && <ArrowDown aria-hidden="true" className="size-3.5" />}
            <Filter
              aria-hidden="true"
              className={cn("size-3.5", active ? "opacity-100" : "opacity-50")}
            />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className="max-h-[var(--radix-popover-content-available-height)] w-72 max-w-[calc(100vw-2rem)] space-y-3 overflow-y-auto"
        role="dialog"
      >
        <p className="text-sm font-semibold">{label}</p>
        {onSort && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                onSort("asc")
                setOpen(false)
              }}
            >
              <ArrowUp aria-hidden="true" className="mr-1 size-4" />
              {t("adminDashboard.columnFilters.ascending", "Ascending")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                onSort("desc")
                setOpen(false)
              }}
            >
              <ArrowDown aria-hidden="true" className="mr-1 size-4" />
              {t("adminDashboard.columnFilters.descending", "Descending")}
            </Button>
          </div>
        )}
        {textSearch && (
          <Input
            placeholder={t("adminDashboard.columnFilters.contains", "Contains…")}
            value={draft.search ?? ""}
            aria-label={t("adminDashboard.columnFilters.searchColumn", "Search {{column}}", {
              column: label,
            })}
            onChange={(event) => setDraft({ ...draft, search: event.target.value })}
          />
        )}
        {options && (
          <>
            <Input
              aria-label={t("adminDashboard.columnFilters.searchValues", "Search filter values")}
              placeholder={t("adminDashboard.columnFilters.searchValues", "Search filter values")}
              value={optionSearch}
              onChange={(event) => setOptionSearch(event.target.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setDraft(
                    excludeFromAll
                      ? { ...draft, selected: null, excluded: [] }
                      : { ...draft, selected: null }
                  )
                }
              >
                {t("adminDashboard.columnFilters.selectAll", "Select all")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setDraft(
                    excludeFromAll
                      ? { ...draft, selected: [], excluded: [] }
                      : { ...draft, selected: [] }
                  )
                }
              >
                {t("adminDashboard.columnFilters.selectNone", "Select none")}
              </Button>
            </div>
            <div
              aria-label={t("adminDashboard.columnFilters.values", "Values")}
              className="max-h-52 space-y-1 overflow-y-auto"
              role="group"
            >
              {visibleOptions?.map((option) => (
                <label
                  key={option.value}
                  className="hover:bg-accent flex min-h-11 cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm sm:min-h-9"
                >
                  <input
                    className="size-4 shrink-0 accent-current"
                    type="checkbox"
                    checked={
                      draft.selected == null
                        ? !(draft.excluded ?? []).includes(option.value)
                        : draft.selected.includes(option.value)
                    }
                    onChange={(event) => {
                      if (excludeFromAll && draft.selected == null) {
                        const excluded = new Set(draft.excluded ?? [])
                        if (event.target.checked) excluded.delete(option.value)
                        else excluded.add(option.value)
                        setDraft({ ...draft, selected: null, excluded: [...excluded] })
                        return
                      }
                      const selected = new Set(draft.selected ?? options.map((item) => item.value))
                      if (event.target.checked) selected.add(option.value)
                      else selected.delete(option.value)
                      setDraft({ ...draft, selected: [...selected] })
                    }}
                  />
                  <span className="min-w-0 flex-1 break-words">{option.label}</span>
                </label>
              ))}
              {visibleOptions?.length === 0 && (
                <p className="text-muted-foreground px-2 py-3 text-sm">
                  {t("adminDashboard.columnFilters.noValues", "No matching values")}
                </p>
              )}
            </div>
          </>
        )}
        {numericRange && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs" htmlFor={`${inputId}-min`}>
                {t("adminDashboard.columnFilters.minimum", "Minimum")}
              </label>
              <Input
                id={`${inputId}-min`}
                type="number"
                value={draft.minimum ?? ""}
                onChange={(event) => setDraft({ ...draft, minimum: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs" htmlFor={`${inputId}-max`}>
                {t("adminDashboard.columnFilters.maximum", "Maximum")}
              </label>
              <Input
                id={`${inputId}-max`}
                type="number"
                value={draft.maximum ?? ""}
                onChange={(event) => setDraft({ ...draft, maximum: event.target.value })}
              />
            </div>
            {invalidRange && (
              <p className="text-destructive col-span-2 text-xs" role="alert">
                {t(
                  "adminDashboard.columnFilters.invalidRange",
                  "Enter a valid minimum and maximum."
                )}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-between gap-2 border-t pt-3">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onClear()
              setOpen(false)
            }}
          >
            {t("adminDashboard.columnFilters.clear", "Clear column")}
          </Button>
          <Button
            disabled={invalidRange}
            size="sm"
            onClick={() => {
              onApply(draft)
              setOpen(false)
            }}
          >
            {t("adminDashboard.columnFilters.apply", "Apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
